'use client';

/**
 * ObeyaPlanLensRoomsNarrativeCard — the model's reading, verbatim. This card never writes, edits or
 * re-derives interpretive prose. The reading arrives on its own strategy, so
 * the lens cards around it paint from facts while it is still being written;
 * until then this card shows its title and a quiet pending line.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensRoomsNarrativeCard
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { usePlanRooms } from './use-plan-rooms';
import { usePlanRoomsReading } from './use-plan-reading';

export function ObeyaPlanLensRoomsNarrativeCard() {
  const { contentKey } = useCardIdentity();
  const { d } = usePlanRooms();
  const { mcpId, strategy, narrative, isLoading, queryKey } = usePlanRoomsReading();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_rooms_narrative',
    status: (narrative ? 'loaded' : mcpId && strategy && isLoading ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: narrative ?? 'Rooms reading',
    json: narrative ? { narrative } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, narrative, mcpId, strategy, isLoading]));

  if (!mcpId || !d) return null;
  if (!narrative && !isLoading) return null;

  return (
    <div className="flex flex-col gap-3 rounded-xl border-l-4 border-primary bg-primary/10 px-5 py-4">
      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-lg bg-primary/10 text-primary"><Sparkles className="h-4 w-4" /></div>
        <div className={cn(text.heading)}>{d.labels.rooms_reading_title}</div>
      </div>
      {narrative
        ? <p className={cn(text.body, 'leading-relaxed')}>{narrative}</p>
        : <p className={cn(text.muted, 'animate-pulse')}>{d.labels.reading_pending}</p>}
    </div>
  );
}
